import { Box, VStack, Text } from '@chakra-ui/react';
import { BaseAlert, BaseAlertProps } from '@cks-internal/react-base';
import { EntityNoteModel } from '../../../../globalModels/entityNotes/entityNoteModel';
import { EntityNoteType } from '../../../../enums/common/EntityNoteType';

type Props = Pick<BaseAlertProps, 'status'> & {
  notes: EntityNoteModel[];
};

/**
 * Displays grouped entity notes in separate Chakra UI Cards.
 *
 * Each note type is shown as an Alert header inside a Card, with the notes
 * listed below in an ordered list. Notes include metadata for creator and date.
 *
 * @component
 * @param {Props} props
 * @param {BaseAlertProps['status']} props.status - The visual status/color of the alert headers.
 * @param {EntityNoteModel[]} props.notes - List of notes; will be grouped by EntityNoteType.
 * @returns {JSX.Element|null} A vertical stack of Cards or null if no notes.
 */

export const EntityNoteViewer = ({ status, notes }: Props) => {
  if (!notes || notes.length === 0) {
    return null;
  }

  // Note type display labels mapping
  const noteTypeLabels: { [key in EntityNoteType]: string } = {
    [EntityNoteType.Basic]: 'Basic',
    [EntityNoteType.AgentNote]: 'Agent',
    [EntityNoteType.CodeRed]: 'Code Red',
    [EntityNoteType.Issue]: 'Issue',
    [EntityNoteType.Hotel]: 'Hotel',
    [EntityNoteType.Client]: 'Client',
    [EntityNoteType.Frontier]: 'Frontier',
    [EntityNoteType.OBTNote]: 'OBT Note'
  };

  // Group notes by their type
  const grouped = notes.reduce<Record<EntityNoteType, EntityNoteModel[]>>(
    (acc, note) => {
      const key = note.EntityNoteType;
      if (!acc[key]) acc[key] = [];
      acc[key].push(note);
      return acc;
    },
    {} as Record<EntityNoteType, EntityNoteModel[]>
  );

  return (
    <VStack gap={3} width='100%'>
      {Object.entries(grouped).map(([typeKey, items]) => {
        const type = parseInt(typeKey) as EntityNoteType;
        const typeLabel = noteTypeLabels[type] || 'Unknown';
        return (
          <BaseAlert key={typeKey} status={status} mb={2}>
            <VStack alignItems='flex-start' gap={1} width='100%'>
              <Box fontWeight='bold' fontSize='md'>
                {typeLabel}
              </Box>
              <VStack alignItems='flex-start' gap={1} width='100%'>
                {items.map((item, idx) => {
                  const date = item.CreatedOn ? new Date(item.CreatedOn).toLocaleDateString() : '';
                  const author = item.CreatedByName || item.CreatedByUserName || 'Unknown';
                  return (
                    <Box key={idx} width='100%'>
                      <Text fontSize='sm' mb={0}>
                        {item.Note}{' '}
                        <Text as='span' fontSize='xs' color='gray.500' fontStyle='italic'>
                          — {author}
                          {date ? ` on ${date}` : ''}
                        </Text>
                      </Text>
                    </Box>
                  );
                })}
              </VStack>
            </VStack>
          </BaseAlert>
        );
      })}
    </VStack>
  );
};
